import { useState } from "react";
import {
  Typography,
  Stack,
  Skeleton,
  Box,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useUnitsFormatter } from "hooks/formatters";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import { useTenant } from "hooks/settings";
import { HeatSeries } from "src/store/api/generatedApi";
import { OverviewCard } from "./OverviewCard";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { CostSparkline } from "../strategyDashboard/heats/cells/CostSparkline";
import { TotalCostDiffCell } from "../strategyDashboard/heats/cells/TotalCostDiffCell";
import { ChemistryViolationsCells } from "../strategyDashboard/heats/cells/ChemistryViolationsCells";
import { ChemistryElementProvider } from "components/strategyDashboard/heats/chemistryTab/ChemistryElementContext";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { RequireTenantSettings } from "src/routes/authentication";
import {
  dateToMidnightRange,
  useHeatsForTimeRange,
} from "hooks/usePrecachedHeats";
import { StrikeTrafficLightCell } from "components/strategyDashboard/heats/cells/StrikeTrafficLightCell";
import { useTenantData } from "contexts/tenant";

const sortByTapTime = (a: HeatSeries, b: HeatSeries) => {
  return (
    (a.tap_timestamp ?? a.start_timestamp).localeCompare(
      b.tap_timestamp ?? b.start_timestamp
    ) * -1
  );
};

export const HeatsOfTheDay = ({ date }: { date: Dayjs }) => {
  const { t } = useTranslation();
  const tenant = useTenant();
  const { toLocalDatetime, localTimezone } = useLocalDatetime();
  const { format } = useNumberSerialiser();
  const formatUnits = useUnitsFormatter(false);
  const tenantData = useTenantData();
  const heatDayStartHour = tenantData.heat_day_start_hour;

  const [selectedSummaryName, setSelectedSummaryName] = useState("");
  const timeRange = dateToMidnightRange(date, localTimezone, heatDayStartHour);
  const { data, isFetching } = useHeatsForTimeRange({
    timeRange,
    filterMode: "fully_contained",
  });

  const summaryKeys = Object.keys(data?.available_mix_summaries ?? {});
  const effectiveSummaryName =
    selectedSummaryName !== "" ? selectedSummaryName : (summaryKeys[0] ?? "");

  const scrapEyeOnly = summaryKeys.includes("liftStrikes");

  const totalHeats = data?.total ?? 0;
  const heats = data?.series ?? [];
  const uniqueGrades = new Set(heats.map((heat) => heat.steel_grade)).size;
  const availableSources = data?.available_mix_sources ?? [];
  const availableConstraints = data?.available_sample_constraints ?? [];

  const getTappedMass = (heat: HeatSeries) => {
    const tappedMassSummary = heat.summary_data.find((summary) =>
      summary.name.toLowerCase().includes("tapped")
    );
    return tappedMassSummary?.value
      ? format(tappedMassSummary.value, {
          decimalPlaces: tappedMassSummary.decimal_places ?? 1,
        }) + formatUnits("mass", false)
      : "-";
  };

  const formatTimeRange = (heat: HeatSeries) => {
    const startTime = toLocalDatetime(heat.start_timestamp).split(" ")[1];
    if (!heat.tap_timestamp) return startTime;
    const tapTime = toLocalDatetime(heat.tap_timestamp).split(" ")[1];
    return `${startTime?.toString() ?? ""} - ${tapTime?.toString() ?? ""}`;
  };

  const baseColumns: GridColDef<HeatSeries>[] = [
    {
      field: "heat_id",
      headerName: t("heat"),
      width: 60,
      display: "flex",
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ fontWeight: "fontWeightSemiBold" }}>
          #{row.heat_id}
        </Typography>
      ),
    },
    {
      field: "steel_grade",
      headerName: t("steelGrade"),
      width: 90,
      display: "flex",
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant="body2">{row.steel_grade}</Typography>
      ),
    },
    {
      field: "time",
      headerName: t("time"),
      width: 90,
      display: "flex",
      sortable: false,
      valueGetter: (_, row) => formatTimeRange(row),
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {value}
        </Typography>
      ),
    },
  ];

  const massColumns: GridColDef<HeatSeries>[] = [
    {
      field: "tapped_mass",
      headerName: t("tappedMass"),
      width: 90,
      align: "right",
      headerAlign: "right",
      display: "flex",
      sortable: false,
      valueGetter: (_, row) => getTappedMass(row),
      renderCell: ({ value }) => (
        <Typography variant="body2">{value}</Typography>
      ),
    },
    {
      field: "trend",
      headerName: t("trend"),
      width: 110,
      display: "flex",
      sortable: false,
      renderCell: ({ row }) =>
        effectiveSummaryName && availableSources.length > 1 ? (
          <CostSparkline
            row={row}
            sources={availableSources}
            selectedSummaryName={effectiveSummaryName}
            ignoredMaterials={new Set()}
            width={80}
            height={20}
          />
        ) : null,
    },
    {
      field: "diff",
      headerName: t("diff"),
      width: 60,
      display: "flex",
      sortable: false,
      renderCell: ({ row }) =>
        effectiveSummaryName && availableSources.length > 1 ? (
          <TotalCostDiffCell
            row={row}
            sources={availableSources}
            selectedSummaryName={effectiveSummaryName}
            ignoredMaterials={new Set()}
            formatNumber={format}
          />
        ) : null,
    },
    {
      field: "chemistry_violations",
      headerName: t("chemistryViolations"),
      flex: 1,
      minWidth: 150,
      display: "flex",
      sortable: false,
      renderCell: ({ row }) => (
        <ChemistryViolationsCells
          row={row}
          t={t}
          availableConstraints={availableConstraints}
          finalSampleOnly
          wrap
        />
      ),
    },
  ];

  const grabsColumns: GridColDef<HeatSeries>[] = [
    {
      field: "specific_cost",
      headerName: scrapEyeOnly
        ? ""
        : t("specificCostOverPlanned", {
            specificCostUnit: formatUnits("specific_cost", false),
          }),
      flex: 1,
      minWidth: 120,
      display: "flex",
      sortable: false,
      renderCell: ({ row }) =>
        scrapEyeOnly ? (
          <StrikeTrafficLightCell row={row} ignoredMaterials={new Set()} />
        ) : effectiveSummaryName && availableSources.length > 1 ? (
          <TotalCostDiffCell
            row={row}
            sources={availableSources}
            selectedSummaryName="$t(chargedPrice)"
            ignoredMaterials={new Set()}
            formatNumber={format}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              fontWeight: "fontWeightMedium",
            }}
          >
            -
          </Box>
        ),
    },
  ];

  const columns: GridColDef<HeatSeries>[] = [
    ...baseColumns,
    ...(tenantData.mix_quantity === "mass" ? massColumns : []),
    ...(tenantData.mix_quantity === "grabs" ? grabsColumns : []),
  ];

  const sortedHeats = [...heats].sort(sortByTapTime);

  const headerContent = (
    <Stack textAlign="right" sx={{ ml: "auto" }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {t("totalHeats")}
      </Typography>
      {isFetching ? (
        <Skeleton variant="text" height={30} width={60} />
      ) : (
        <Typography
          variant="h4"
          sx={{ fontWeight: "fontWeightBold", color: "primary.main" }}
        >
          {totalHeats}
        </Typography>
      )}
    </Stack>
  );

  return (
    <ChemistryElementProvider
      availableElements={data?.available_sample_constraints ?? []}
    >
      <OverviewCard
        title={t("heatsOfTheDay")}
        icon={WhatshotIcon}
        headerContent={headerContent}
        buttonText={t("viewAllHeats")}
        buttonHref={`/${tenant}/dashboard/heats`}
        delay={0.1}
      >
        <Stack
          sx={{ px: 2, pb: 2, height: "100%", gap: 1, boxSizing: "border-box" }}
        >
          {isFetching ? (
            <Skeleton height={200} variant="rectangular" />
          ) : totalHeats === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                {t("noHeatsForThisDay")}
              </Typography>
            </Box>
          ) : (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("steelGrades")}: {uniqueGrades}
                </Typography>
                <RequireTenantSettings settings={{ mix_quantity: "mass" }}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                      value={effectiveSummaryName}
                      onChange={(e) => {
                        setSelectedSummaryName(e.target.value);
                      }}
                      displayEmpty
                      variant="standard"
                      sx={{
                        fontSize: "0.75rem",
                        "& .MuiSelect-select": {
                          py: 0.25,
                          fontSize: "0.75rem",
                        },
                        "& .MuiInput-underline:before": {
                          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        },
                        "& .MuiInput-underline:hover:before": {
                          borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
                        },
                      }}
                    >
                      {data?.available_mix_summaries
                        ? Object.entries(data.available_mix_summaries).map(
                            ([summaryName, unitType]) => (
                              <MenuItem
                                key={summaryName}
                                value={summaryName}
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {unitType === null
                                  ? t(summaryName)
                                  : `${t(summaryName)} (${formatUnits(
                                      unitType,
                                      false
                                    )})`}
                              </MenuItem>
                            )
                          )
                        : null}
                    </Select>
                  </FormControl>
                </RequireTenantSettings>
              </Stack>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <HotkeyCompliantDataGrid
                  rows={sortedHeats}
                  columns={columns}
                  getRowId={(row) => row.heat_id}
                  getRowHeight={() => "auto"}
                  sx={{ border: "none", height: "100%" }}
                />
              </Box>
            </>
          )}
        </Stack>
      </OverviewCard>
    </ChemistryElementProvider>
  );
};
